import { convexQuery, useConvexAction } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import { Button } from '@rekrytering/ui/components/button'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

type Props = {
  readonly candidateId: Id<'candidates'>
}

const STATUS_LABELS = {
  pending: 'Pending',
  accepted: 'Accepted',
  declined: 'Declined',
} as const

const STATUS_COLORS = {
  pending: 'text-yellow-600 bg-yellow-50',
  accepted: 'text-green-600 bg-green-50',
  declined: 'text-red-600 bg-red-50',
} as const

const formatDate = (timestampMs: number): string => {
  return new Date(timestampMs).toLocaleDateString()
}

export const CandidateOfferPanel = ({ candidateId }: Props) => {
  const [offerBody, setOfferBody] = useState('')
  const [isSending, setIsSending] = useState(false)

  const { data: offers = [] } = useQuery(
    convexQuery(api.features.candidates.job_offers.listMyJobOffers, {
      candidateId,
    }),
  )

  const { mutateAsync: sendOffer } = useMutation({
    mutationFn: useConvexAction(
      api.features.candidates.send_my_job_offer.sendMyJobOffer,
    ),
  })

  const handleSendOffer = async (): Promise<void> => {
    const trimmed = offerBody.trim()
    if (!trimmed) return
    setIsSending(true)
    try {
      await sendOffer({ candidateId, body: trimmed })
      setOfferBody('')
    } finally {
      setIsSending(false)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-medium text-primary">Job offers</h3>

      {offers.length === 0 ? (
        <p className="text-xs text-primary/40">No offers sent yet.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {offers.map((offer) => (
            <li
              key={offer._id}
              className="flex flex-col gap-1 rounded-md border border-border px-3 py-2"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-primary/50">
                  Sent {formatDate(offer.sentAt)}
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[offer.status]}`}
                >
                  {STATUS_LABELS[offer.status]}
                </span>
              </div>
              <p className="line-clamp-2 text-sm text-primary">{offer.body}</p>
              {offer.respondedAt !== undefined && (
                <p className="text-xs text-primary/50">
                  Responded {formatDate(offer.respondedAt)}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-col gap-2">
        <textarea
          className="resize-none rounded-md border border-border bg-background px-3 py-2 text-sm text-primary placeholder:text-primary/40 focus:outline-none focus:ring-1 focus:ring-primary/30"
          rows={3}
          placeholder="Write the offer letter body..."
          value={offerBody}
          onChange={(e) => setOfferBody(e.target.value)}
        />
        <Button
          size="sm"
          onClick={() => void handleSendOffer()}
          disabled={!offerBody.trim() || isSending}
        >
          {isSending ? 'Sending...' : 'Send offer'}
        </Button>
      </div>
    </div>
  )
}
